import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Account-wide storage usage — the "used" half of the pooled allowance meter
// and of the upload preflight's pooled check.
//
// WHAT IS COUNTED (#412): raw photos + the project's source video + its
// rendered output, summed across ALL projects. The previous version summed only
// each project's `meta/coverage.totalSizeBytes`, which the photo-ingest path
// alone writes — so source videos and renders were invisible. Measured on
// staging: deleting 18 projects freed 21.51 GiB of source video and the meter
// did not move, because it was mathematically incapable of moving.
//
// TWO SOURCES, IN ORDER:
//   1. `users/{uid}.storageUsage.totalBytes` — the server-maintained aggregate
//      (packages/functions/shared/storage_usage.py). This is the SAME number
//      the ingest trigger enforces the pool against, so the meter and the
//      server agree by construction rather than by two parallel sums. It is
//      server-only (firestore.rules limits client writes on users/{uid} to
//      displayName/email/photoURL/settings), so it can't be forged.
//   2. A client-side sum over the user's projects, used only while the
//      aggregate is absent — accounts that predate it get one lazily backfilled
//      on their next upload, and this keeps the meter honest in the meantime.
//      Unlike the old rollup it also adds the video/render slots off each
//      project doc, which costs no extra reads (the project list is already
//      fetched).
//
// Either way this is a point-in-time read for display and preflight, not a
// live listener: the authoritative gate is the ingest transaction.

const EMPTY = {
  usedBytes: 0, photoBytes: 0, videoBytes: 0, renderBytes: 0, source: 'empty',
};

// Firestore numbers arrive as numbers, but a hand-edited or half-migrated doc
// can hold anything. Anything non-finite reads as "absent", never as 0 — the
// difference decides whether we trust the aggregate or fall back to summing.
function toBytes(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

// The server aggregate off a user doc, or null when it hasn't been written yet.
// Exported for the enforcement-parity test: this is the one place the client
// decides "does the account have a maintained total".
export function readUsageAggregate(userData) {
  const usage = (userData || {}).storageUsage;
  if (!usage || typeof usage !== 'object') return null;
  const usedBytes = toBytes(usage.totalBytes);
  if (usedBytes === null) return null;
  return {
    usedBytes,
    photoBytes: toBytes(usage.photoBytes) || 0,
    videoBytes: toBytes(usage.videoBytes) || 0,
    renderBytes: toBytes(usage.renderBytes) || 0,
    source: 'aggregate',
  };
}

export async function fetchAccountStorage(uid, {
  queryFn = query,
  collectionFn = collection,
  whereFn = where,
  getDocsFn = getDocs,
  docFn = doc,
  getDocFn = getDoc,
} = {}) {
  if (!uid) return { ...EMPTY };

  const userSnap = await getDocFn(docFn(db, 'users', uid));
  const aggregate = readUsageAggregate(userSnap.exists() ? userSnap.data() : null);
  if (aggregate) return aggregate;

  // No aggregate yet — sum the projects ourselves, including the video and
  // render slots the old rollup ignored.
  const projectsSnap = await getDocsFn(
    queryFn(collectionFn(db, 'projects'), whereFn('userId', '==', uid)),
  );
  const projects = [];
  projectsSnap.forEach((d) => projects.push({ id: d.id, data: d.data() || {} }));

  let photoBytes = 0;
  let videoBytes = 0;
  let renderBytes = 0;
  await Promise.all(projects.map(async ({ id, data }) => {
    // One project holds at most one source video and one rendered output, so
    // the current occupant of each slot IS what that project stores.
    videoBytes += Number(data.video?.sizeBytes) || 0;
    renderBytes += Number(data.editedVideo?.sizeBytes) || 0;
    const cov = await getDocFn(docFn(db, 'projects', id, 'meta', 'coverage'));
    if (cov.exists()) {
      photoBytes += Number((cov.data() || {}).totalSizeBytes) || 0;
    }
  }));

  return {
    usedBytes: photoBytes + videoBytes + renderBytes,
    photoBytes,
    videoBytes,
    renderBytes,
    source: 'computed',
  };
}

export default function useAccountStorage(user, { fetcher = fetchAccountStorage } = {}) {
  const [state, setState] = useState({ ...EMPTY, loading: true });

  // Key on the uid, not the user object — a caller that passes a fresh `user`
  // object each render (common) would otherwise re-run this effect forever.
  const uid = user?.uid || null;

  const load = useCallback(async () => {
    if (!uid) {
      setState({ ...EMPTY, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await fetcher(uid);
      setState({ ...res, loading: false });
    } catch (err) {
      console.error('account storage rollup failed:', err);
      setState((s) => ({ ...s, loading: false }));
    }
  }, [uid, fetcher]);

  useEffect(() => { load(); }, [load]);

  return { ...state, reload: load };
}
